import { useState } from "react";
import axios from "axios";
import { MessageSquare, Loader2, Sparkles, AlertCircle, Quote, Lightbulb, ListChecks } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SENTIMENT_STYLES = {
  positivo: { bar: "bg-green-500", text: "text-green-700" },
  neutral: { bar: "bg-gray-400", text: "text-gray-600" },
  negativo: { bar: "bg-red-500", text: "text-red-700" },
};

export default function CommentsAnalysis({ analysis, canAnalyze = true, onLogin }) {
  const [data, setData] = useState(analysis.comments_analysis || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const runAnalysis = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API}/analysis/${analysis.id}/analyze-comments`);
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudieron analizar los comentarios. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="comments-analysis-section" className="bg-white rounded-2xl shadow-xl p-6 border border-purple-100">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-purple-600" />
          <h3 className="text-xl font-semibold text-gray-800">Análisis de Comentarios</h3>
          {data?.comments_sampled && (
            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-semibold">
              {data.comments_sampled} comentarios analizados
            </span>
          )}
        </div>
        {!data && !loading && canAnalyze && (
          <button
            data-testid="analyze-comments-button"
            onClick={runAnalysis}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-md hover:shadow-lg transition-all"
          >
            <Sparkles className="w-4 h-4" />
            Analizar Comentarios con IA
          </button>
        )}
        {!data && !loading && !canAnalyze && (
          <button
            data-testid="comments-login-button"
            onClick={onLogin}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 transition-all"
          >
            Inicia sesión para analizar comentarios
          </button>
        )}
      </div>

      {!data && !loading && !error && (
        <p className="text-sm text-gray-500">
          Descubre qué opina y qué pide realmente la audiencia analizando los comentarios más populares del video.
        </p>
      )}

      {loading && (
        <div data-testid="comments-loading" className="text-center py-10">
          <Loader2 className="w-8 h-8 text-purple-600 animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-600">Obteniendo y analizando comentarios... (~20-40 seg)</p>
        </div>
      )}

      {error && (
        <div data-testid="comments-error" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2 text-sm">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {data && (
        <div data-testid="comments-results" className="space-y-6 animate-fadeIn">
          {data.summary && (
            <p className="text-gray-700 bg-purple-50 border border-purple-100 rounded-xl px-4 py-3 text-sm">{data.summary}</p>
          )}

          {data.sentiment && (
            <div>
              <h4 className="text-sm font-bold text-gray-700 uppercase mb-3">Sentimiento de la audiencia</h4>
              <div className="space-y-2">
                {Object.entries(data.sentiment).map(([key, value]) => {
                  const style = SENTIMENT_STYLES[key] || SENTIMENT_STYLES.neutral;
                  return (
                    <div key={key} className="flex items-center gap-3">
                      <span className={`w-20 text-sm font-semibold capitalize ${style.text}`}>{key}</span>
                      <div className="flex-1 bg-gray-100 rounded-full h-3">
                        <div className={`${style.bar} h-3 rounded-full transition-all`} style={{ width: `${value}%` }}></div>
                      </div>
                      <span className="w-10 text-sm font-bold text-gray-700 text-right">{value}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-6">
            {data.top_themes?.length > 0 && (
              <div>
                <h4 className="text-sm font-bold text-gray-700 uppercase mb-3">Temas más comentados</h4>
                <div className="space-y-2">
                  {data.top_themes.map((t, i) => (
                    <div key={i} className="bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                      <p className="font-semibold text-sm text-gray-800">{t.theme}</p>
                      <p className="text-xs text-gray-500">{t.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.audience_requests?.length > 0 && (
              <div>
                <h4 className="text-sm font-bold text-gray-700 uppercase mb-3 flex items-center gap-1">
                  <ListChecks className="w-4 h-4 text-purple-600" /> Qué pide la audiencia
                </h4>
                <ul className="space-y-2">
                  {data.audience_requests.map((req, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className="text-purple-500 font-bold mt-0.5">→</span>
                      {req}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {data.content_ideas?.length > 0 && (
            <div>
              <h4 className="text-sm font-bold text-gray-700 uppercase mb-3 flex items-center gap-1">
                <Lightbulb className="w-4 h-4 text-yellow-500" /> Ideas de contenido basadas en comentarios
              </h4>
              <div className="grid md:grid-cols-3 gap-3">
                {data.content_ideas.map((idea, i) => (
                  <div key={i} className="bg-gradient-to-br from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl p-4">
                    <p className="font-bold text-sm text-gray-900 mb-1">{idea.title}</p>
                    <p className="text-xs text-gray-600">{idea.reason}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.notable_comments?.length > 0 && (
            <div>
              <h4 className="text-sm font-bold text-gray-700 uppercase mb-3">Comentarios destacados</h4>
              <div className="space-y-2">
                {data.notable_comments.map((c, i) => (
                  <div key={i} className="flex items-start gap-2 bg-gray-50 rounded-lg px-4 py-3 border-l-4 border-purple-400">
                    <Quote className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-700 italic">{c}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
